import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import Chip from '@mui/material/Chip';

// ── Constants ────────────────────────────────────────────────
const DAYS        = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const HOUR_HEIGHT = 60;   // px per hour = 1 px per minute
const TOTAL_HOURS = 24;
const GRID_HEIGHT = TOTAL_HOURS * HOUR_HEIGHT;
const HOURS       = Array.from({ length: TOTAL_HOURS }, (_, i) => i);
const SCROLL_TO   = 7;    // scroll to 07:00 on mount

// ── Absence config ───────────────────────────────────────────
const ABSENCE_COLORS = {
  VACATION: '#0D9488', SICK: '#F59E0B', COMPENSATION: '#3B82F6',
  MILITARY: '#64748B', CIVIL_SERVICE: '#6366F1',
  MATERNITY: '#8B5CF6', PATERNITY: '#8B5CF6', PARENTAL: '#8B5CF6',
  BEREAVEMENT: '#475569', MARRIAGE: '#EC4899',
  UNPAID: '#94A3B8', OTHER: '#94A3B8',
};
const ABSENCE_LABELS = {
  VACATION: 'Vacation', SICK: 'Sick', COMPENSATION: 'Comp.',
  MILITARY: 'Military', CIVIL_SERVICE: 'Civil Svc',
  MATERNITY: 'Maternity', PATERNITY: 'Paternity', PARENTAL: 'Parental',
  BEREAVEMENT: 'Bereavement', MARRIAGE: 'Marriage',
  UNPAID: 'Unpaid', OTHER: 'Other',
};

// ── Helpers ──────────────────────────────────────────────────
function getWeekDays(anchorDate) {
  const d   = new Date(anchorDate);
  const day = d.getDay();
  const mon = new Date(d);
  mon.setDate(d.getDate() + (day === 0 ? -6 : 1 - day));
  return Array.from({ length: 7 }, (_, i) => {
    const dt = new Date(mon);
    dt.setDate(mon.getDate() + i);
    return dt;
  });
}

function isSameDay(a, b) {
  return a.getFullYear() === b.getFullYear() &&
         a.getMonth()    === b.getMonth()    &&
         a.getDate()     === b.getDate();
}

function toDateStr(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function absenceCoversDay(a, day) {
  const ds = toDateStr(day);
  return ds >= a.startDate && ds <= a.endDate;
}

function fmtEntryDate(dateStr) {
  if (!dateStr) return null;
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d);
}

export function timeToMinutes(timeStr) {
  if (!timeStr) return null;
  const [h, m] = timeStr.split(':').map(Number);
  return h * 60 + (m || 0);
}

function minutesToTime(total) {
  const clamped = Math.max(0, Math.min(Math.round(total), GRID_HEIGHT - 1));
  const h = Math.floor(clamped / 60);
  const m = clamped % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

function snapTo15(min) { return Math.round(min / 15) * 15; }

function fmtDisplay(timeStr) {
  if (!timeStr) return '';
  const [h, m] = timeStr.split(':').map(Number);
  return `${h}:${String(m).padStart(2, '0')}`;
}

function getCurrentMinutes() {
  const n = new Date();
  return n.getHours() * 60 + n.getMinutes();
}

// ── Current time line ────────────────────────────────────────
function CurrentTimeLine() {
  const [min, setMin] = useState(getCurrentMinutes);
  useEffect(() => {
    const t = setInterval(() => setMin(getCurrentMinutes()), 30000);
    return () => clearInterval(t);
  }, []);
  return (
    <Box sx={{
      position: 'absolute', top: min, left: 0, right: 0,
      zIndex: 6, pointerEvents: 'none', display: 'flex', alignItems: 'center',
    }}>
      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#EF4444', flexShrink: 0, ml: '-4px' }} />
      <Box sx={{ flex: 1, height: '1.5px', bgcolor: '#EF4444' }} />
    </Box>
  );
}

// ── Timed entry block (draggable + resizable) ─────────────────
function EntryBlock({ entry, projectColor, onEdit, onDelete, onUpdate, colRef, compact }) {
  const color         = projectColor(entry.projectId);
  const localRef      = useRef(entry);
  const hasDragged    = useRef(false);
  const [local, setLocal] = useState(entry);

  useEffect(() => { localRef.current = entry; setLocal(entry); }, [entry]);

  // ── Drag to move ────────────────────────────────────────
  const handleMouseDown = useCallback((e) => {
    if (e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    hasDragged.current = false;

    const duration   = timeToMinutes(localRef.current.endTime) - timeToMinutes(localRef.current.startTime);
    const rect0      = colRef.current.getBoundingClientRect();
    const offsetInBlock = e.clientY - rect0.top - timeToMinutes(localRef.current.startTime);

    const onMove = (e) => {
      hasDragged.current = true;
      const rect    = colRef.current.getBoundingClientRect();
      const rawTop  = e.clientY - rect.top - offsetInBlock;
      const newStart = snapTo15(Math.max(0, Math.min(rawTop, GRID_HEIGHT - duration)));
      const updated  = {
        ...localRef.current,
        startTime:    minutesToTime(newStart),
        endTime:      minutesToTime(newStart + duration),
        workingHours: duration / 60,
      };
      localRef.current = updated;
      setLocal({ ...updated });
    };

    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      if (hasDragged.current) {
        onUpdate({ ...localRef.current });
      } else {
        onEdit(entry);
      }
    };

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [colRef, entry, onEdit, onUpdate]);

  // ── Resize from bottom ──────────────────────────────────
  const handleResizeDown = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    const startY      = e.clientY;
    const origEnd     = timeToMinutes(localRef.current.endTime);
    const minStart    = timeToMinutes(localRef.current.startTime);

    const onMove = (e) => {
      const newEnd = snapTo15(Math.max(minStart + 15, Math.min(origEnd + (e.clientY - startY), GRID_HEIGHT)));
      const updated = {
        ...localRef.current,
        endTime:      minutesToTime(newEnd),
        workingHours: (newEnd - minStart) / 60,
      };
      localRef.current = updated;
      setLocal({ ...updated });
    };

    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      onUpdate({ ...localRef.current });
    };

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [onUpdate]);

  const startMin = timeToMinutes(local.startTime);
  const endMin   = timeToMinutes(local.endTime);
  if (startMin === null || endMin === null) return null;

  const top      = startMin;
  const height   = Math.max(endMin - startMin, 20);
  const showTime = height >= 36;
  const showDesc = height >= 60 && entry.description;

  return (
    <Box
      onMouseDown={handleMouseDown}
      onClick={(e) => e.stopPropagation()}
      sx={{
        position: 'absolute',
        top:    `${top}px`,
        left:   compact ? '2px' : '4px',
        right:  compact ? '2px' : '4px',
        height: `${height}px`,
        bgcolor:    `${color}18`,
        border:     `1px solid ${color}45`,
        borderLeft: `3px solid ${color}`,
        borderRadius: '5px',
        cursor: 'grab',
        overflow: 'hidden',
        zIndex: 3,
        transition: 'box-shadow 0.12s',
        '&:active': { cursor: 'grabbing', zIndex: 10 },
        '&:hover':  { boxShadow: `0 2px 10px ${color}30`, borderColor: `${color}80` },
        '&:hover .ea': { opacity: 1 },
        '& .ea': { opacity: 0, transition: 'opacity 0.12s' },
        userSelect: 'none',
      }}
    >
      {/* Content */}
      <Box sx={{ px: 0.75, pt: 0.4, overflow: 'hidden', height: 'calc(100% - 10px)' }}>
        <Typography sx={{ fontSize: 10, fontWeight: 700, color, lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {entry.projectName}
        </Typography>
        {showTime && (
          <Typography sx={{ fontSize: 9, color: `${color}BB`, lineHeight: 1.3 }}>
            {fmtDisplay(local.startTime)} – {fmtDisplay(local.endTime)}
          </Typography>
        )}
        {showDesc && (
          <Typography sx={{ fontSize: 9, color: '#64748B', mt: 0.15, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {entry.description}
          </Typography>
        )}
      </Box>

      {/* Hover actions */}
      <Box className="ea" sx={{ position: 'absolute', top: 2, right: 2, display: 'flex', gap: 0.15 }}>
        <IconButton size="small" onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => { e.stopPropagation(); onEdit(entry); }}
          sx={{ p: '2px', color: '#94A3B8', bgcolor: 'rgba(255,255,255,0.9)', borderRadius: '3px', '&:hover': { color: '#427898' } }}>
          <EditIcon sx={{ fontSize: 10 }} />
        </IconButton>
        <IconButton size="small" onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => { e.stopPropagation(); onDelete(entry); }}
          sx={{ p: '2px', color: '#94A3B8', bgcolor: 'rgba(255,255,255,0.9)', borderRadius: '3px', '&:hover': { color: '#EF4444' } }}>
          <DeleteIcon sx={{ fontSize: 10 }} />
        </IconButton>
      </Box>

      {/* Resize handle */}
      <Box
        onMouseDown={handleResizeDown}
        onClick={(e) => e.stopPropagation()}
        sx={{
          position: 'absolute', bottom: 0, left: 0, right: 0, height: 10,
          cursor: 'ns-resize', display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        {height > 28 && (
          <Box sx={{ width: 18, height: 2, bgcolor: color, borderRadius: 1, opacity: 0.35 }} />
        )}
      </Box>
    </Box>
  );
}

// ── Unscheduled (no time) entry pill ─────────────────────────
function UnscheduledPill({ entry, projectColor, onEdit, compact }) {
  const color = projectColor(entry.projectId);
  return (
    <Box onClick={() => onEdit(entry)} sx={{
      px: 0.75, py: 0.2, mb: 0.2,
      bgcolor: `${color}14`, borderLeft: `2.5px solid ${color}`,
      borderRadius: '3px', cursor: 'pointer',
      display: 'flex', alignItems: 'center', gap: 0.5,
      '&:hover': { bgcolor: `${color}22` },
    }}>
      <Typography sx={{ fontSize: 10, fontWeight: 600, color, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {entry.projectName}
      </Typography>
      {entry.workingHours != null && (
        <Typography sx={{ fontSize: 9, color: '#94A3B8', flexShrink: 0 }}>
          {entry.workingHours % 1 === 0 ? entry.workingHours : entry.workingHours.toFixed(1)}h
        </Typography>
      )}
    </Box>
  );
}

// ── Day column ───────────────────────────────────────────────
function DayColumn({ day, entries, absences = [], isToday, isWeekend, projectColor, onAddDay, onEdit, onDelete, onUpdate, isLast }) {
  const colRef      = useRef(null);
  const timed       = entries.filter(e => e.startTime && e.endTime);
  const unscheduled = entries.filter(e => !e.startTime || !e.endTime);
  const approved    = absences.filter(a => a.status === 'APPROVED');

  const handleSlotClick = (hour) => {
    const st = `${String(hour).padStart(2, '0')}:00`;
    onAddDay(day, st);
  };

  return (
    <Box ref={colRef} sx={{
      flex: 1, position: 'relative', minWidth: 80,
      borderRight: isLast ? 'none' : '1px solid #EEF2F6',
      bgcolor: isToday ? '#F8FEFF' : isWeekend ? '#FAFBFC' : '#fff',
    }}>
      {/* Absence overlays (behind everything) */}
      {approved.map(a => {
        const color  = ABSENCE_COLORS[a.type] || '#94A3B8';
        const top    = a.halfDay && a.halfDayPeriod === 'AFTERNOON' ? GRID_HEIGHT / 2 : 0;
        const height = a.halfDay ? GRID_HEIGHT / 2 : GRID_HEIGHT;
        return (
          <Box key={a.id} sx={{
            position: 'absolute', top, left: 0, right: 0, height,
            bgcolor: `${color}12`, borderLeft: `2px solid ${color}35`,
            zIndex: 1, pointerEvents: 'none',
          }} />
        );
      })}

      {/* Hour grid cells */}
      {HOURS.map(h => (
        <Box key={h} onClick={() => handleSlotClick(h)} sx={{
          height: HOUR_HEIGHT,
          borderTop: h === 0 ? 'none' : `1px solid ${h % 3 === 0 ? '#E8ECF0' : '#F3F5F8'}`,
          cursor: 'crosshair',
          '&:hover': { bgcolor: isWeekend ? '#F5F5F8' : '#EFF6FF30' },
        }} />
      ))}

      {/* Today line */}
      {isToday && <CurrentTimeLine />}

      {/* Timed entry blocks */}
      {timed.map(e => (
        <EntryBlock key={e.id} entry={e} projectColor={projectColor}
          onEdit={onEdit} onDelete={onDelete} onUpdate={onUpdate}
          colRef={colRef} compact />
      ))}

      {/* Unscheduled pills overlay at top */}
      {unscheduled.length > 0 && (
        <Box sx={{ position: 'absolute', top: 2, left: 2, right: 2, zIndex: 4, pointerEvents: 'none' }}>
          <Box sx={{ pointerEvents: 'auto' }}>
            {unscheduled.slice(0, 2).map(e => (
              <UnscheduledPill key={e.id} entry={e} projectColor={projectColor} onEdit={onEdit} compact />
            ))}
            {unscheduled.length > 2 && (
              <Typography sx={{ fontSize: 9, color: '#94A3B8', pl: 0.5 }}>+{unscheduled.length - 2} more</Typography>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
}

// ── Main week view ───────────────────────────────────────────
export default function TTWeekView({ currentDate, entries, absences = [], projectColor, onAddDay, onEdit, onDelete, onUpdate }) {
  const weekDays   = getWeekDays(currentDate);
  const today      = new Date();
  const scrollRef  = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = SCROLL_TO * HOUR_HEIGHT;
  }, []);

  const dayEntries = weekDays.map(day =>
    entries.filter(e => {
      const ed = e.entryDate ? fmtEntryDate(e.entryDate) : null;
      return ed && isSameDay(ed, day);
    })
  );

  const dayAbsences = weekDays.map(day => absences.filter(a => absenceCoversDay(a, day)));

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', border: '1px solid #EEF2F6', borderRadius: 2, overflow: 'hidden', bgcolor: '#fff', boxShadow: '0 1px 4px rgba(15,23,42,0.04)' }}>

      {/* ── Day header row ── */}
      <Box sx={{ display: 'flex', borderBottom: '2px solid #EEF2F6', flexShrink: 0, bgcolor: '#fff', zIndex: 5 }}>

        {/* Gutter corner */}
        <Box sx={{ width: 52, flexShrink: 0, borderRight: '1px solid #EEF2F6' }} />

        {/* Day name + date + pills */}
        {weekDays.map((day, i) => {
          const isToday   = isSameDay(day, today);
          const isWeekend = i >= 5;
          const totalH    = dayEntries[i].reduce((s, e) => s + (e.workingHours || 0), 0);
          const approved  = dayAbsences[i].filter(a => a.status === 'APPROVED');
          return (
            <Box key={i} sx={{
              flex: 1, px: 1, pt: 0.75, pb: 0.75, minWidth: 80,
              borderRight: i < 6 ? '1px solid #EEF2F6' : 'none',
              bgcolor: isToday ? '#F0F9FF' : 'transparent',
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
                <Typography sx={{ fontSize: 10, fontWeight: 700, color: isWeekend ? '#CBD5E1' : '#94A3B8', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  {DAYS[i]}
                </Typography>
                <Box sx={{
                  width: 22, height: 22, borderRadius: '50%',
                  bgcolor: isToday ? '#427898' : 'transparent',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: isToday ? '#fff' : isWeekend ? '#CBD5E1' : '#0F172A' }}>
                    {day.getDate()}
                  </Typography>
                </Box>
                <Box sx={{ flex: 1 }} />
                {totalH > 0 && (
                  <Typography sx={{ fontSize: 10, fontWeight: 700, color: totalH >= 8 ? '#16A34A' : '#F59E0B' }}>
                    {totalH % 1 === 0 ? totalH : totalH.toFixed(1)}h
                  </Typography>
                )}
                <Tooltip title="Add entry">
                  <IconButton size="small" onClick={() => onAddDay(day)}
                    sx={{ p: 0.2, color: '#CBD5E1', '&:hover': { color: '#427898', bgcolor: '#EFF6FF' } }}>
                    <AddIcon sx={{ fontSize: 13 }} />
                  </IconButton>
                </Tooltip>
              </Box>
              {/* Absence chips */}
              {approved.slice(0, 2).map((a, j) => {
                const color = ABSENCE_COLORS[a.type] || '#94A3B8';
                return (
                  <Tooltip key={j} title={`${a.employeeFirstName ?? ''} ${a.employeeLastName ?? ''}`.trim() || 'Absence'}>
                    <Box sx={{
                      display: 'inline-flex', alignItems: 'center', mr: 0.3,
                      px: 0.5, py: 0.1, borderRadius: 0.75,
                      bgcolor: `${color}18`, border: `1px solid ${color}35`,
                      cursor: 'default',
                    }}>
                      <Typography sx={{ fontSize: 8.5, fontWeight: 700, color, lineHeight: 1.4 }}>
                        {ABSENCE_LABELS[a.type] || a.type}{a.halfDay ? ' ½' : ''}
                      </Typography>
                    </Box>
                  </Tooltip>
                );
              })}
              {approved.length > 2 && (
                <Typography sx={{ fontSize: 8, color: '#94A3B8', display: 'inline' }}>+{approved.length - 2}</Typography>
              )}
            </Box>
          );
        })}
      </Box>

      {/* ── Time grid (scrollable) ── */}
      <Box ref={scrollRef} sx={{ flex: 1, overflow: 'auto', display: 'flex', height: '68vh', minHeight: 300 }}>

        {/* Time gutter */}
        <Box sx={{ width: 52, flexShrink: 0, borderRight: '1px solid #EEF2F6', userSelect: 'none', bgcolor: '#FAFBFC' }}>
          {HOURS.map(h => (
            <Box key={h} sx={{ height: HOUR_HEIGHT, position: 'relative' }}>
              {h > 0 && (
                <Typography sx={{ position: 'absolute', top: -8, right: 8, fontSize: 9, fontWeight: 600, color: '#B0BEC5', lineHeight: 1, letterSpacing: 0.2 }}>
                  {String(h).padStart(2, '0')}:00
                </Typography>
              )}
            </Box>
          ))}
        </Box>

        {/* Day columns */}
        <Box sx={{ flex: 1, display: 'flex' }}>
          {weekDays.map((day, di) => (
            <DayColumn
              key={di}
              day={day}
              entries={dayEntries[di]}
              absences={dayAbsences[di]}
              isToday={isSameDay(day, today)}
              isWeekend={di >= 5}
              isLast={di === 6}
              projectColor={projectColor}
              onAddDay={onAddDay}
              onEdit={onEdit}
              onDelete={onDelete}
              onUpdate={onUpdate}
            />
          ))}
        </Box>
      </Box>
    </Box>
  );
}

// ── EntryCard (used by TTAgendaView) ─────────────────────────
export function EntryCard({ entry, projectColor, onEdit, onDelete, compact }) {
  const color = projectColor(entry.projectId);
  const hasTime = entry.startTime && entry.endTime;
  return (
    <Box onClick={() => !compact && onEdit(entry)} sx={{
      borderRadius: 1.5,
      border: '1px solid #EEF2F6',
      borderLeft: `3px solid ${color}`,
      p: compact ? 1 : 1.5,
      bgcolor: '#fff',
      cursor: compact ? 'default' : 'pointer',
      transition: 'border-color 0.15s, box-shadow 0.15s',
      '&:hover': { borderColor: '#CBD5E1', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' },
      '&:hover .ea2': { opacity: 1 },
      '& .ea2': { opacity: 0, transition: 'opacity 0.15s' },
    }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 0.5 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 11, fontWeight: 700, color, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {entry.projectName}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mt: 0.2 }}>
            <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#0F172A', lineHeight: 1.2 }}>
              {entry.workingHours != null ? (entry.workingHours % 1 === 0 ? entry.workingHours : entry.workingHours.toFixed(1)) : '—'}h
            </Typography>
            {hasTime && (
              <Typography sx={{ fontSize: 11, color: '#94A3B8' }}>
                {fmtDisplay(entry.startTime)} – {fmtDisplay(entry.endTime)}
              </Typography>
            )}
          </Box>
        </Box>
        <Box className="ea2" sx={{ display: 'flex', gap: 0.1, flexShrink: 0 }}>
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); onEdit(entry); }}
            sx={{ p: 0.2, color: '#94A3B8', '&:hover': { color: '#427898' } }}>
            <EditIcon sx={{ fontSize: 12 }} />
          </IconButton>
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); onDelete(entry); }}
            sx={{ p: 0.2, color: '#94A3B8', '&:hover': { color: '#EF4444' } }}>
            <DeleteIcon sx={{ fontSize: 12 }} />
          </IconButton>
        </Box>
      </Box>

      {entry.description && !compact && (
        <Typography sx={{ fontSize: 12, color: '#64748B', mt: 0.5, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
          {entry.description}
        </Typography>
      )}
      {entry.description && compact && (
        <Typography sx={{ fontSize: 11, color: '#94A3B8', mt: 0.25, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {entry.description}
        </Typography>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
        {entry.billable !== false ? (
          <Chip size="small" icon={<AttachMoneyIcon style={{ fontSize: 10 }} />} label="Billable"
            sx={{ bgcolor: '#F0FDF4', color: '#16A34A', fontSize: 9, height: 16, '& .MuiChip-icon': { color: '#16A34A', ml: '4px' }, '& .MuiChip-label': { px: 0.75 } }} />
        ) : (
          <Chip size="small" label="Internal"
            sx={{ bgcolor: '#F8FAFC', color: '#94A3B8', fontSize: 9, height: 16, '& .MuiChip-label': { px: 0.75 } }} />
        )}
        {entry.invoiced && (
          <Chip size="small" label="Invoiced"
            sx={{ bgcolor: '#EFF6FF', color: '#1565C0', fontSize: 9, height: 16, '& .MuiChip-label': { px: 0.75 } }} />
        )}
      </Box>
    </Box>
  );
}
